import { useState, useRef, useEffect, useMemo, SyntheticEvent } from "react"
import { useTranslation } from "react-i18next"

import Selector from "@/components/form/selector/Selector"
import Toggle from "@/components/form/toggle/Toggle"

import { DEFAULT_ACTION_TYPES, DEFAULT_MAP_TYPES } from "@/app/report/common/tacticMapType"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import {
  DEFAULT_TACTIC_MAP_SETTINGS,
  getTemplateTacticMapSections,
  resolveTemplateTacticMapSection,
} from "@/app/report/common/sections/tacticMap"
import { colors } from "@/components/colors"
import type { ChartOptionsPanelProps } from "./types"
import { MetricSelector } from "./SharedWidgets"

// 액션 표 컬럼(TacticMapSection DEFAULT_COLUMNS 순서). No. 열은 항상 보이므로 목록에 없다
const ACTION_TABLE_COLUMNS = [
  "uniformNumber",
  "occurrence",
  "duration",
  "distance",
  "maxSpeed",
  "maxAcceleration",
  "maxDeceleration",
  "interval",
]

const MAP_COLOR_PRESETS = [
  colors.red["700"],
  colors.blue["800"],
  colors.green["800"],
  colors.orange["700"],
  colors.purple["900"],
]

/**
 * Map 은 리포트 Tactic Map 섹션과 같은 컴포넌트로 그린다 (일감 #75, 5단계). 맵 종류·액션 종류·색은
 * Report Filters 에서 고른 리포트 템플릿의 Tactic Map 설정이 기본이고 여기서 아이템별로 바꾼다.
 * 종류 목록은 리포트 필터와 같은 DEFAULT_MAP_TYPES / DEFAULT_ACTION_TYPES 를 쓴다.
 */
export default function MapOptions({
  item,
  onOptionChange,
  onDataSourceChange,
}: ChartOptionsPanelProps) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const { reportTemplate } = usePdfDataContext()
  const [colorPickerOpen, setColorPickerOpen] = useState(false)
  const colorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (colorRef.current && !colorRef.current.contains(e.target as Node)) {
        setColorPickerOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)

    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const templateSections = useMemo(
    () => getTemplateTacticMapSections(reportTemplate),
    [reportTemplate],
  )
  const currentSection = useMemo(
    () => resolveTemplateTacticMapSection(templateSections, item.dataSource.sectionIndex),
    [templateSections, item.dataSource.sectionIndex],
  )
  const settings = currentSection?.settings ?? DEFAULT_TACTIC_MAP_SETTINGS
  const sectionOptions = useMemo(
    () =>
      templateSections.map((section, order) => ({
        value: String(section.index),
        label: section.settings.title
          ? t(section.settings.title)
          : `${t("actionMap", "Action Map")} ${order + 1}`,
      })),
    [templateSections, t],
  )

  // 리포트 필터(TacticMapFilter)와 같은 목록·번역
  const mapTypeOptions = DEFAULT_MAP_TYPES.map((mt) => ({
    value: mt.value,
    label: translateKey(mt.label) || t(mt.label, mt.label),
  }))
  const actionTypeOptions = DEFAULT_ACTION_TYPES.map((at) => ({
    value: at.value,
    label: translateKey(at.label) || t(at.label, at.label),
  }))

  // Table 과 같은 Table Scale — Auto: 한 표로 길게, Manual: 세션 묶음마다 아이템 하나 (2026-09-29 요청)
  const scaleOptions = [
    { value: "auto", label: t("layoutAuto", "Auto") },
    { value: "manual", label: t("layoutManual", "Manual") },
  ]

  const currentMapType = item.options.mapType ?? settings.defaultMapType
  const currentActionType = item.options.actionType ?? settings.defaultActionType
  const currentMapColor = item.options.mapColor ?? settings.color
  const colorPresets = MAP_COLOR_PRESETS.includes(settings.color)
    ? MAP_COLOR_PRESETS
    : [settings.color, ...MAP_COLOR_PRESETS]

  return (
    <>
      {templateSections.length > 1 && (
        <div className="PropertyPanel__field">
          <Selector
            slotProps={{ popper: { className: "PropertyPanel__menu" } }}
            label={t("actionMap", "Action Map")}
            options={sectionOptions}
            value={
              sectionOptions.find((option) => option.value === String(currentSection?.index)) ||
              sectionOptions[0]
            }
            onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
              if (newVal) {
                onDataSourceChange("sectionIndex", Number(newVal.value))
              }
            }}
            placeholder={t("select")}
          />
        </div>
      )}

      <div className="PropertyPanel__field">
        <Selector
          slotProps={{ popper: { className: "PropertyPanel__menu" } }}
          label={t("mapType", "Map Type")}
          options={mapTypeOptions}
          value={mapTypeOptions.find((o) => o.value === currentMapType) || mapTypeOptions[0]}
          onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
            if (newVal) {
              onOptionChange("mapType", newVal.value)
            }
          }}
          placeholder={t("select")}
        />
      </div>

      <div className="PropertyPanel__field">
        <Selector
          slotProps={{ popper: { className: "PropertyPanel__menu" } }}
          label={t("actionType", "Action Type")}
          options={actionTypeOptions}
          value={
            actionTypeOptions.find((o) => o.value === currentActionType) || actionTypeOptions[0]
          }
          onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
            if (newVal) {
              onOptionChange("actionType", newVal.value)
            }
          }}
          placeholder={t("select")}
        />
      </div>

      {/* Layout — Auto: 한 표로 길게, Manual: 세션 묶음마다 아이템 하나. 액션 표 유무와 상관없이 보여 준다 (2026-10-01 요청) */}
      <div className="PropertyPanel__field">
        <Selector
          slotProps={{ popper: { className: "PropertyPanel__menu" } }}
          label={t("layout", "Layout")}
          options={scaleOptions}
          value={
            scaleOptions.find((o) => o.value === (item.options.tableScale ?? "auto")) ||
            scaleOptions[0]
          }
          onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
            if (newVal) {
              onOptionChange("tableScale", newVal.value)
            }
          }}
          placeholder={t("select")}
        />
      </div>

      {/* 액션 표 보기 — 끄면 맵만 칸에 맞춰 그린다. 켜면 표가 오른쪽에 붙고 칸 높이가 표에 맞춰진다 (2026-09-29 개편) */}
      <div className="PropertyPanel__field PropertyPanel__field--toggle">
        <Toggle
          checked={item.options.showActionTable ?? true}
          onChange={(e) => onOptionChange("showActionTable", e.target.checked)}
        />
        <label>{t("showActionTable", "Show action table")}</label>
      </div>

      {(item.options.showActionTable ?? true) && (
        <>
          {/* 표 컬럼 — Table 의 지표 선택과 같은 드롭다운. 빼면 숨기고 No. 열은 항상 보인다 (2026-09-29 요청) */}
          <MetricSelector
            label={t("columns", "Columns")}
            selectedMetrics={item.options.actionTableColumns ?? ACTION_TABLE_COLUMNS}
            metricOptions={ACTION_TABLE_COLUMNS.map((key) => ({
              value: key,
              label: translateKey(key) || t(key, key),
            }))}
            onAdd={(key) => {
              const current = item.options.actionTableColumns ?? ACTION_TABLE_COLUMNS
              onOptionChange(
                "actionTableColumns",
                ACTION_TABLE_COLUMNS.filter((k) => k === key || current.includes(k)),
              )
            }}
            onRemove={(key) =>
              onOptionChange(
                "actionTableColumns",
                (item.options.actionTableColumns ?? ACTION_TABLE_COLUMNS).filter((k) => k !== key),
              )
            }
            translateKey={(key) => translateKey(key) || t(key, key)}
          />
        </>
      )}

      <div className="PropertyPanel__field" ref={colorRef}>
        <label>{t("mapColor", "Map Color")}</label>
        <button
          className="PropertyPanel__swatch PropertyPanel__swatch--large"
          style={{ backgroundColor: currentMapColor }}
          onClick={() => setColorPickerOpen((prev) => !prev)}
        />
        {colorPickerOpen && (
          <div className="PropertyPanel__color-swatches">
            {colorPresets.map((color) => (
              <button
                key={color}
                className={`PropertyPanel__swatch ${currentMapColor === color ? "PropertyPanel__swatch--selected" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  onOptionChange("mapColor", color)
                  setColorPickerOpen(false)
                }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}
